<template>
  <div class="password-input-container">
    <label v-if="label" class="password-input-label" :for="inputId">{{ label }}<span v-if="required" class="required-indicator">*</span></label>
    <div class="password-input" :class="inputClass">
      <input
        :id="inputId"
        :type="showPassword ? 'text' : 'password'"
        class="password-input-field"
        :placeholder="placeholder"
        :value="modelValue"
        :disabled="disabled"
        autocomplete="nope"
        data-lpignore="true"
        data-form-type="other"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="handleBlur"
        ref="input"
      >
      <button
        type="button"
        class="toggle-visibility"
        @click="toggleVisibility"
        :disabled="disabled"
        tabindex="-1"
      >
        <vue-feather :type="showPassword ? 'eye-off' : 'eye'" size="18" />
      </button>
    </div>
    <span v-if="error" class="password-input-error">{{ error }}</span>
  </div>
</template>

<script>
import VueFeather from 'vue-feather'

let inputId = 0

export default {
  name: 'PasswordInput',
  components: {
    'vue-feather': VueFeather
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputId: `password-input-${++inputId}`,
      isFocused: false,
      showPassword: false
    }
  },
  computed: {
    inputClass() {
      return {
        'password-input--focused': this.isFocused,
        'password-input--disabled': this.disabled,
        'password-input--error': this.error
      }
    }
  },
  methods: {
    handleInput(event) {
      this.$emit('update:modelValue', event.target.value)
    },
    handleBlur() {
      this.isFocused = false
      this.$emit('blur')
    },
    toggleVisibility() {
      this.showPassword = !this.showPassword
    },
    focus() {
      this.$refs.input.focus()
    }
  },
  emits: ['update:modelValue', 'blur']
}
</script>

<style scoped>
.password-input-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.password-input-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
}

.required-indicator {
  color: #dc2626;
  margin-left: 2px;
}

.password-input {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 45px;
  background: #FFF;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.password-input:hover:not(.password-input--disabled):not(.password-input--error) {
  border-color: #9ca3af;
}

.password-input--focused:not(.password-input--error) {
  border-color: var(--color-primary, #6a16f0);
  box-shadow: 0 0 0 3px rgba(106, 22, 240, 0.1);
}

.password-input--disabled {
  background: #fafafa;
  cursor: not-allowed;
}

.password-input--error {
  border-color: #dc2626;
}

.password-input--error.password-input--focused {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.password-input-field {
  width: 100%;
  height: 100%;
  padding: 0 40px 0 14px;
  font-size: 1rem;
  font-weight: 400;
  color: #374151;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
  box-sizing: border-box;
}

.password-input-field::placeholder {
  color: #9ca3af;
}

.password-input-field:disabled {
  cursor: not-allowed;
}

.toggle-visibility {
  position: absolute;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
}

.toggle-visibility:hover:not(:disabled) {
  background: #f3f4f6;
  color: #374151;
}

.toggle-visibility:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.password-input-error {
  font-size: 0.8125rem;
  color: #dc2626;
}
</style>
